import type { JSX } from 'react';
import { useMemo } from 'react';

import {
  createDpdpApi,
  DeleteAccountHelp,
  DpdpDepsProvider,
  MePrivacyScreen,
  NoticeScreen,
} from '@/features/dpdp';
import type { DpdpDeps } from '@/features/dpdp';
import { createWebFetcher } from '@/lib/api/fetcher';
import type { Fetcher } from '@/lib/api/fetcher';

import { useClientDeps } from './deps';

/**
 * The BP-03 §6 client-app rows: the public `/privacy` and the signed-in `/me/privacy`.
 *
 * `/privacy` is **public**: `GET /dpdp/notice` needs no session (a data principal has to be
 * able to read the notice before consenting to anything), and the tenant is resolved from
 * the `Host` header server-side. So this route has no guard at all — which is the point.
 *
 * `/me/privacy` is §14.6's stub: BP-04 owns client login, so until it lands the screen shows
 * the "ask at the front desk" copy above the same notice.
 */

/** The transport the notice is fetched over (see `apps/staff/bp03.tsx`). */
export function clientFetcher(fetcher: Fetcher | undefined): Fetcher {
  return fetcher ?? createWebFetcher();
}

/** The dpdp feature's dependencies for the client app. */
export function useClientDpdpDeps(): DpdpDeps {
  const deps = useClientDeps();
  const api = useMemo(() => createDpdpApi(clientFetcher(deps.fetcher)), [deps.fetcher]);
  return { api };
}

/** §6 client `/privacy` — the public notice, "plain text". */
export function PrivacyRoute(): JSX.Element {
  return (
    <DpdpDepsProvider deps={useClientDpdpDeps()}>
      <NoticeScreen />
    </DpdpDepsProvider>
  );
}

/** §6 client `/me/privacy` — §14.6's stub copy above the notice. */
export function MePrivacyRoute(): JSX.Element {
  return (
    <DpdpDepsProvider deps={useClientDpdpDeps()}>
      <MePrivacyScreen />
    </DpdpDepsProvider>
  );
}

/**
 * BP-11 R17's public `/delete-account` — the web URL Google Play requires
 * beside the in-app path.
 *
 * No provider and no api: the page answers without a session, on purpose, so
 * it needs nothing from the transport. See `DeleteAccountHelp` for why it
 * takes no phone number either.
 */
export function DeleteAccountRoute(): JSX.Element {
  return <DeleteAccountHelp />;
}
